import type { CourseCardItem, CourseCardVariant } from '@/features/course-card';

import type { CourseCardWidgetAttribute, CourseCardWidgetProps } from './types';

export const OBSERVED_COURSE_CARD_WIDGET_ATTRIBUTES: CourseCardWidgetAttribute[] = [
  'course',
  'variant',
  'is-in-cart',
  'is-favorite',
  'is-logged-in',
  'cyber-active',
  'selectable',
  'selected',
  'selection-name',
  'selection-value',
  'hide-actions',
  'hide-price',
  'prioritize-image',
  'action-label',
  'checkout-label',
  'cart-intent',
  'selected-course-id',
];

const BOOLEAN_TRUE_VALUES = new Set(['', '1', 'true', 'yes']);

export const readBooleanAttribute = (element: HTMLElement, name: string): boolean => {
  const value = element.getAttribute(name);
  if (value === null) return false;
  return BOOLEAN_TRUE_VALUES.has(value.toLocaleLowerCase('en-US'));
};

const readOptionalStringAttribute = (element: HTMLElement, name: string): string | undefined => {
  const value = element.getAttribute(name);
  return value === null || value === '' ? undefined : value;
};

export const isCourseCardItem = (value: unknown): value is CourseCardItem => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.slug === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.imageUrl === 'string' &&
    typeof candidate.price === 'number' &&
    typeof candidate.currency === 'string' &&
    typeof candidate.modality === 'string' &&
    typeof candidate.rating === 'number' &&
    typeof candidate.studentsCount === 'number'
  );
};

export const parseCourseCardAttribute = (value: string | null): CourseCardItem | null => {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    return isCourseCardItem(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const readCourseCardVariantAttribute = (value: string | null): CourseCardVariant => {
  if (value === 'row') return 'row';
  return 'card';
};

export function readCourseCardWidgetProps(
  element: HTMLElement,
  courseProperty: CourseCardItem | null,
): CourseCardWidgetProps {
  return {
    course: courseProperty ?? parseCourseCardAttribute(element.getAttribute('course')),
    variant: readCourseCardVariantAttribute(element.getAttribute('variant')),
    isFavorite: readBooleanAttribute(element, 'is-favorite'),
    isInCart: readBooleanAttribute(element, 'is-in-cart'),
    isLoggedIn: readBooleanAttribute(element, 'is-logged-in'),
    isCyberActive: readBooleanAttribute(element, 'cyber-active'),
    isSelectable: readBooleanAttribute(element, 'selectable'),
    isSelected: readBooleanAttribute(element, 'selected'),
    selectionName: readOptionalStringAttribute(element, 'selection-name'),
    selectionValue: readOptionalStringAttribute(element, 'selection-value'),
    hideActions: readBooleanAttribute(element, 'hide-actions'),
    hidePrice: readBooleanAttribute(element, 'hide-price'),
    prioritizeImage: readBooleanAttribute(element, 'prioritize-image'),
    actionLabel: readOptionalStringAttribute(element, 'action-label'),
    checkoutLabel: readOptionalStringAttribute(element, 'checkout-label'),
    cartIntent: element.getAttribute('cart-intent') === 'cross-sell' ? 'cross-sell' : 'default',
    selectedCourseId: readOptionalStringAttribute(element, 'selected-course-id'),
  };
}
